import * as React from 'react';
import { useEffect, useState } from 'react';
import { Spin, Tag, Tabs, Typography } from 'antd';
import { Line } from '@ant-design/plots';
import { useAtom } from 'jotai';
import { generate } from '@ant-design/colors';
import axiosApiInstance from '../../../api/axiosClient';
import { YT_ICON, IG_ICON, TT_ICON, formatFollowers } from './utils';
import { YT_COLOR, IG_COLOR, TT_COLOR } from '../../../common/constants';
import { creatorAtom, creatorTagsAtomById } from './atoms';
import { getPaletteColor } from './utils';
import ContactLinkField from './ContactLinkField';
import DemographicsPanel, { CHART_CARD_STYLE, CHART_HEIGHT } from './DemographicsPanel';
import type { TagCategory } from './CreatorViewEdit';

// ── Shared constants ─────────────────────────────────────────────────────────

const PLATFORM_COLOR: Record<number, string> = { 1: YT_COLOR, 2: IG_COLOR, 3: TT_COLOR };
const PLATFORM_LABEL: Record<number, string> = { 1: 'YouTube', 2: 'Instagram', 3: 'TikTok' };
const PLATFORM_NAME: Record<number, string> = { 1: 'YouTube', 2: 'Instagram', 3: 'TikTok' };
const PLATFORM_ICON_MAP: Record<number, React.ReactNode> = { 1: YT_ICON, 2: IG_ICON, 3: TT_ICON };

// ── Fetch-history helpers ─────────────────────────────────────────────────────

interface FetchHistoryEntry {
    id: number;
    account_id: number;
    action_type: string;
    status: string;
    error_message?: string;
    data_after?: string;
    source: string;
    created_at: string;
}

interface ParsedHistoryPoint {
    date: string;
    followers: number;
    engagement_rate?: number;
}

function weekOfMonth(d: Date): number {
    return Math.min(Math.ceil(d.getDate() / 7), 4);
}

function bucketKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-W${weekOfMonth(d)}`;
}

function bucketLabel(d: Date): string {
    const month = d.toLocaleDateString('en-GB', { month: 'short' });
    return `${month} W${weekOfMonth(d)}`;
}

function parseEntry(e: FetchHistoryEntry, label: string): ParsedHistoryPoint | null {
    const raw = e.data_after!;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'number') {
            return parsed > 0 ? { date: label, followers: parsed } : null;
        }
        if (typeof parsed === 'object' && parsed !== null) {
            if (parsed.platform != null && parsed.gender != null) {
                return null;
            }
            if (typeof parsed.followers === 'number' && parsed.followers > 0) {
                const point: ParsedHistoryPoint = {
                    date: label,
                    followers: parsed.followers,
                };
                if (hasFetchedEngagementRate(parsed.engagement_rate)) {
                    point.engagement_rate = parsed.engagement_rate;
                }
                return point;
            }
            return null;
        }
    } catch {
        const n = parseInt(raw, 10);
        if (!isNaN(n) && n > 0) {
            return { date: label, followers: n };
        }
    }
    return null;
}

/** Backend stores engagement rate as a decimal (e.g. 0.0725 = 7.25%). */
function engagementRatePercent(rate: number): number {
    return rate * 100;
}

/** Zero means not fetched yet (DB default / no monthly engagement run). */
function hasFetchedEngagementRate(rate: number | undefined | null): rate is number {
    return rate != null && rate > 0;
}

function formatCount(n: number | undefined | null): string {
    if (n == null || n <= 0) return '—';
    return formatFollowers(n);
}

type MetricCard = { label: string; value: string; accent: string };

function buildPlatformMetricCards(
    platformId: number,
    acct: API.Creators.CreatorSocialMediaAccount,
    latestFollowers: number | undefined,
    currentER: number | undefined,
    color: string,
): MetricCard[] {
    const cards: MetricCard[] = [
        {
            label: platformId === 1 ? 'Subscribers' : 'Followers',
            value: latestFollowers != null ? formatCount(latestFollowers) : '—',
            accent: color,
        },
    ];

    if (platformId === 1) {
        if (acct.video_count) {
            cards.push({ label: 'Videos', value: formatCount(acct.video_count), accent: '#595959' });
        }
        if (acct.view_count) {
            cards.push({ label: 'Total Views', value: formatCount(acct.view_count), accent: '#595959' });
        }
    } else if (platformId === 2) {
        if (hasFetchedEngagementRate(currentER)) {
            cards.push({
                label: 'Engagement Rate',
                value: `${engagementRatePercent(currentER).toFixed(2)}%`,
                accent: '#fadb14',
            });
        }
        if (acct.video_count) {
            cards.push({ label: 'Posts', value: formatCount(acct.video_count), accent: '#595959' });
        }
    } else if (platformId === 3) {
        if (hasFetchedEngagementRate(currentER)) {
            cards.push({
                label: 'Engagement Rate',
                value: `${engagementRatePercent(currentER).toFixed(2)}%`,
                accent: '#fadb14',
            });
        }
        if (acct.total_likes) {
            cards.push({ label: 'Total Likes', value: formatCount(acct.total_likes), accent: '#595959' });
        }
        if (acct.video_count) {
            cards.push({ label: 'Videos', value: formatCount(acct.video_count), accent: '#595959' });
        }
    }

    return cards;
}

function monthBucketKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthBucketLabel(d: Date): string {
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

const THREE_MONTHS_MS = 3 * 30 * 24 * 60 * 60 * 1000;
const SIX_MONTHS_MS = 6 * 30 * 24 * 60 * 60 * 1000;

function followerHistoryEntries(entries: FetchHistoryEntry[]): FetchHistoryEntry[] {
    const sixMonthsAgo = Date.now() - SIX_MONTHS_MS;
    return [...entries]
        .filter((e) => e.status === 'success' && e.data_after && e.action_type === 'fetch_followers')
        .filter((e) => new Date(e.created_at).getTime() >= sixMonthsAgo)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

function engagementHistoryEntries(entries: FetchHistoryEntry[]): FetchHistoryEntry[] {
    const sixMonthsAgo = Date.now() - SIX_MONTHS_MS;
    return [...entries]
        .filter((e) => e.status === 'success' && e.data_after && e.action_type === 'fetch_engagement')
        .filter((e) => new Date(e.created_at).getTime() >= sixMonthsAgo)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

function historySpanExceedsThreeMonths(entries: FetchHistoryEntry[]): boolean {
    if (entries.length < 2) return false;
    const times = entries.map((e) => new Date(e.created_at).getTime());
    return Math.max(...times) - Math.min(...times) > THREE_MONTHS_MS;
}

function bucketHistoryPoints(sorted: FetchHistoryEntry[]): ParsedHistoryPoint[] {
    if (sorted.length === 0) return [];

    const useMonthly = historySpanExceedsThreeMonths(sorted);
    const buckets = new Map<string, { entry: FetchHistoryEntry; label: string }>();
    for (const e of sorted) {
        const d = new Date(e.created_at);
        const key = useMonthly ? monthBucketKey(d) : bucketKey(d);
        const label = useMonthly ? monthBucketLabel(d) : bucketLabel(d);
        buckets.set(key, { entry: e, label });
    }
    return Array.from(buckets.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, { entry, label }]) => parseEntry(entry, label))
        .filter((p): p is ParsedHistoryPoint => p != null);
}

function engagementRateFromEntry(e: FetchHistoryEntry): number | undefined {
    if (!e.data_after) return undefined;
    try {
        const parsed = JSON.parse(e.data_after);
        if (typeof parsed === 'object' && parsed !== null && hasFetchedEngagementRate(parsed.engagement_rate)) {
            return parsed.engagement_rate;
        }
    } catch {
        // ignore malformed history payloads
    }
    return undefined;
}

/** Buckets only fetches with a non-zero engagement rate — zero values never appear on ER diagrams. */
function bucketEngagementRateHistoryPoints(sorted: FetchHistoryEntry[]): ParsedHistoryPoint[] {
    if (sorted.length === 0) return [];

    const useMonthly = historySpanExceedsThreeMonths(sorted);
    const buckets = new Map<string, { entry: FetchHistoryEntry; label: string }>();
    for (const e of sorted) {
        if (!hasFetchedEngagementRate(engagementRateFromEntry(e))) {
            continue;
        }
        const d = new Date(e.created_at);
        const key = useMonthly ? monthBucketKey(d) : bucketKey(d);
        const label = useMonthly ? monthBucketLabel(d) : bucketLabel(d);
        buckets.set(key, { entry: e, label });
    }
    return Array.from(buckets.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, { entry, label }]) => parseEntry(entry, label))
        .filter((p): p is ParsedHistoryPoint => p != null && hasFetchedEngagementRate(p.engagement_rate));
}

function parseHistoryPoints(entries: FetchHistoryEntry[]): ParsedHistoryPoint[] {
    return bucketHistoryPoints(followerHistoryEntries(entries));
}

function parseEngagementHistoryPoints(entries: FetchHistoryEntry[]): ParsedHistoryPoint[] {
    return bucketEngagementRateHistoryPoints(engagementHistoryEntries(entries));
}

function parseFollowerEngagementHistoryPoints(entries: FetchHistoryEntry[]): ParsedHistoryPoint[] {
    return bucketEngagementRateHistoryPoints(followerHistoryEntries(entries));
}

const PLATFORM_WITH_DEMOGRAPHICS = new Set([1, 2, 3]); // YouTube, Instagram, TikTok

// ── PlatformStatsPanel ────────────────────────────────────────────────────────

export const PlatformStatsPanel: React.FC<{
    accounts: API.Creators.CreatorSocialMediaAccount[];
    platformId: number;
}> = ({ accounts, platformId }) => {
    const acct = accounts?.find(
        (a) => a.platform_id === platformId || a.platform?.id === platformId || a.platform?.name === PLATFORM_NAME[platformId]
    );
    const [history, setHistory] = useState<FetchHistoryEntry[]>([]);
    const [demographics, setDemographics] = useState<API.Creators.AudienceDemographics | null>(null);
    const [loading, setLoading] = useState(false);
    const [demographicsLoading, setDemographicsLoading] = useState(false);
    const color = PLATFORM_COLOR[platformId] ?? '#666';

    useEffect(() => {
        if (!acct?.id) return;
        setLoading(true);
        axiosApiInstance
            .get(`/api/creators/accounts/${acct.id}/fetch-history`)
            .then((res) => setHistory(res.data ?? []))
            .catch(() => setHistory([]))
            .finally(() => setLoading(false));
    }, [acct?.id]);

    useEffect(() => {
        if (!acct?.id || !PLATFORM_WITH_DEMOGRAPHICS.has(platformId)) {
            setDemographics(null);
            return;
        }
        setDemographicsLoading(true);
        axiosApiInstance
            .get(`/api/creators/accounts/${acct.id}/demographics`, { skipErrorNotification: true })
            .then((res) => setDemographics(res.data ?? null))
            .catch(() => setDemographics(null))
            .finally(() => setDemographicsLoading(false));
    }, [acct?.id, platformId]);

    if (!acct) {
        return (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#bbb' }}>
                No {PLATFORM_LABEL[platformId]} account linked to this creator.
            </div>
        );
    }

    const points = parseHistoryPoints(history);
    const latestFollowers = acct.following ?? (points.length > 0 ? points[points.length - 1].followers : undefined);
    const erPoints = platformId === 2
        ? parseEngagementHistoryPoints(history)
        : platformId === 3
            ? parseFollowerEngagementHistoryPoints(history)
            : [];
    const hasERHistory = erPoints.length > 0;
    const currentER = (platformId === 2 || platformId === 3) && hasFetchedEngagementRate(acct.engagement_rate)
        ? acct.engagement_rate
        : undefined;

    const showAudienceInsights = PLATFORM_WITH_DEMOGRAPHICS.has(platformId);
    const insightChartHeight = showAudienceInsights ? CHART_HEIGHT : 220;
    const insightCardStyle = showAudienceInsights
        ? CHART_CARD_STYLE
        : { background: '#fafafa', borderRadius: 8, padding: 16, border: '1px solid #f0f0f0' };

    const followersChartCfg = {
        data: points.map((p) => ({ date: p.date, value: p.followers })),
        xField: 'date',
        yField: 'value',
        color,
        smooth: true,
        point: { size: 3 },
        yAxis: { label: { formatter: (v: string) => Number(v) >= 1000 ? `${(Number(v) / 1000).toFixed(0)}K` : v } },
        tooltip: {
            formatter: (d: { value?: number }) => ({
                name: 'Followers',
                value: (d.value ?? 0).toLocaleString(),
            }),
        },
        height: insightChartHeight,
        autoFit: true,
    };

    const erChartCfg = {
        data: erPoints.map((p) => ({ date: p.date, value: engagementRatePercent(p.engagement_rate!) })),
        xField: 'date',
        yField: 'value',
        color: '#fadb14',
        smooth: true,
        point: { size: 3 },
        yAxis: { label: { formatter: (v: string) => `${v}%` } },
        tooltip: {
            formatter: (d: { value?: number }) => ({
                name: 'Eng. Rate',
                value: `${(d.value ?? 0).toFixed(2)}%`,
            }),
        },
        height: insightChartHeight,
        autoFit: true,
    };

    const growthCard = !loading && points.length > 0 ? (
        <div style={insightCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Audience Growth</span>
                {latestFollowers != null && (
                    <span style={{ fontWeight: 700, fontSize: 15, color }}>
                        {latestFollowers >= 1000 ? `${(latestFollowers / 1000).toFixed(1)}K` : latestFollowers}
                    </span>
                )}
            </div>
            <Line {...followersChartCfg} />
        </div>
    ) : null;

    const erCard = !loading && hasERHistory ? (
        <div style={insightCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#555' }}>Engagement Rate Growth</span>
                {hasFetchedEngagementRate(currentER) && (
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#fadb14' }}>{engagementRatePercent(currentER).toFixed(2)}%</span>
                )}
            </div>
            <Line {...erChartCfg} />
        </div>
    ) : null;

    const cards = buildPlatformMetricCards(platformId, acct, latestFollowers, currentER, color);

    return (
        <div style={{ paddingTop: 8 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 16px', marginBottom: 20,
                background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8,
                borderLeft: `4px solid ${color}`,
            }}>
                <span style={{ fontSize: 22, lineHeight: 1 }}>{PLATFORM_ICON_MAP[platformId]}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography.Text strong style={{ fontSize: 15, display: 'block' }}>
                        {acct.title || acct.platform_account_id || '—'}
                    </Typography.Text>
                    {acct.key && (
                        <Typography.Link href={acct.key} target="_blank" style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {acct.key}
                        </Typography.Link>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
                {cards.map((c) => (
                    <div key={c.label} style={{
                        background: '#fff',
                        border: '1px solid #f0f0f0',
                        borderTop: `3px solid ${c.accent}`,
                        borderRadius: 8,
                        padding: '12px 14px',
                    }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{c.value}</div>
                    </div>
                ))}
            </div>

            {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

            {!loading && points.length === 0 && !showAudienceInsights && (
                <div style={{ textAlign: 'center', color: '#bbb', padding: '24px 0' }}>No fetch history yet.</div>
            )}

            {!loading && showAudienceInsights && (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 12 }}>
                        Audience Insights
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                        {growthCard ?? (
                            <div style={{ ...CHART_CARD_STYLE, textAlign: 'center', color: '#bbb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                No fetch history yet.
                            </div>
                        )}
                        {erCard}
                        <DemographicsPanel
                            embedded
                            chartHeight={CHART_HEIGHT}
                            data={demographics}
                            loading={demographicsLoading}
                        />
                    </div>
                </div>
            )}

            {!loading && !showAudienceInsights && points.length > 0 && growthCard}
        </div>
    );
};

// ── CreatorDetailContent ──────────────────────────────────────────────────────

/**
 * Renders the full read-only creator detail view (tabs: Creator / YT / IG / TT).
 * Uses Jotai atoms internally so the parent must be wrapped in a <Suspense>.
 */
const CreatorDetailContent: React.FC<{ id: string | undefined }> = ({ id }) => {
    const [{ data: creator }] = useAtom(creatorAtom(id));
    const [{ data: tagOptions }] = useAtom(creatorTagsAtomById(id));

    const [activeTab, setActiveTab] = useState<'info' | 'yt' | 'ig' | 'tt'>('info');

    const transformedTagOptions = React.useMemo((): TagCategory[] => {
        if (!tagOptions) return [];
        const transformed = (tagOptions as TagCategory[]).map((option) => ({
            ...option,
            label: option.label === 'Specific' ? 'Content Category' : option.label,
        }));
        return [...transformed].sort((a, b) => {
            const priority = ['Prime Channel Category', 'Content Category', 'Channel Type'];
            const ai = priority.indexOf(a.label);
            const bi = priority.indexOf(b.label);
            if (ai !== -1 && bi !== -1) return ai - bi;
            if (ai !== -1) return -1;
            if (bi !== -1) return 1;
            return 0;
        });
    }, [tagOptions]);

    const S_CARD: React.CSSProperties = {
        border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 20px', background: '#fff', marginBottom: 12,
    };
    const S_TITLE: React.CSSProperties = {
        fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase',
        letterSpacing: '0.5px', paddingBottom: 10, marginBottom: 14, borderBottom: '1px solid #f5f5f5',
    };

    return (
        <>
            {/* Platform tabs */}
            <Tabs
                activeKey={activeTab}
                onChange={(k) => setActiveTab(k as typeof activeTab)}
                style={{ marginBottom: 16 }}
                items={[
                    { key: 'info', label: 'Creator' },
                    { key: 'yt', label: <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{YT_ICON}<span style={{ fontSize: 12 }}>YouTube</span></span> },
                    { key: 'ig', label: <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{IG_ICON}<span style={{ fontSize: 12 }}>Instagram</span></span> },
                    { key: 'tt', label: <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{TT_ICON}<span style={{ fontSize: 12 }}>TikTok</span></span> },
                ]}
            />

            {activeTab === 'yt' && <PlatformStatsPanel accounts={creator?.accounts ?? []} platformId={1} />}
            {activeTab === 'ig' && <PlatformStatsPanel accounts={creator?.accounts ?? []} platformId={2} />}
            {activeTab === 'tt' && <PlatformStatsPanel accounts={creator?.accounts ?? []} platformId={3} />}

            {/* Creator info tab */}
            <div style={{ display: activeTab !== 'info' ? 'none' : 'block' }}>

                {/* Profile header */}
                {(() => {
                    const mainAcct = creator?.accounts?.find(a =>
                        (a.platform_id ?? a.platform?.id) === creator.main_platform_id
                    ) ?? creator?.accounts?.find(a => a.image);
                    const igAcct = creator?.accounts?.find(a => (a.platform_id ?? a.platform?.id) === 2);
                    const ttAcct = creator?.accounts?.find(a => (a.platform_id ?? a.platform?.id) === 3);
                    const bio = igAcct?.bio ?? ttAcct?.bio ?? mainAcct?.bio;
                    const aiDescription = creator?.ai_description?.trim();
                    const imgSrc: string | undefined = mainAcct?.image
                        ? `data:image/jpeg;base64,${mainAcct.image}`
                        : undefined;
                    if (!imgSrc && !bio && !aiDescription) return null;
                    const twoCol = !!bio && !!aiDescription;
                    return (
                        <div style={{
                            display: 'flex', alignItems: 'flex-start', gap: 18,
                            padding: '16px 20px', marginBottom: 16,
                            background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8,
                        }}>
                            {imgSrc && (
                                <img
                                    src={imgSrc}
                                    alt={creator?.creator ?? ''}
                                    style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #f0f0f0' }}
                                />
                            )}
                            <div style={{
                                flex: 1, minWidth: 0,
                                display: 'grid',
                                gridTemplateColumns: twoCol ? '1fr 1fr' : '1fr',
                                gap: 24,
                            }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{creator?.creator}</div>
                                    {bio ? (
                                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{bio}</div>
                                    ) : (
                                        !aiDescription && <div style={{ fontSize: 13, color: '#bbb' }}>No channel bio available.</div>
                                    )}
                                </div>
                                {aiDescription && (
                                    <div style={{
                                        minWidth: 0,
                                        borderLeft: twoCol ? '1px solid #f0f0f0' : 'none',
                                        paddingLeft: twoCol ? 20 : 0,
                                    }}>
                                        <div style={{
                                            fontSize: 11, fontWeight: 600, color: '#8c8c8c',
                                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
                                        }}>
                                            About
                                        </div>
                                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                            {aiDescription}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Social accounts table */}
                <table style={{
                    width: '100%', borderCollapse: 'collapse', marginBottom: 20,
                    background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, fontSize: 13,
                }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #e8e8e8', background: '#fafafa' }}>
                            <th style={{ padding: '8px 14px', fontWeight: 600, color: '#888', fontSize: 11, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Platform</th>
                            <th style={{ padding: '8px 14px', fontWeight: 600, color: '#888', fontSize: 11, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Account</th>
                            <th style={{ padding: '8px 14px', fontWeight: 600, color: '#888', fontSize: 11, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '1px solid #f0f0f0' }}>Followers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(creator?.accounts ?? []).length === 0 && (
                            <tr><td colSpan={3} style={{ padding: '16px 14px', textAlign: 'center', color: '#bbb' }}>No social media accounts linked.</td></tr>
                        )}
                        {(creator?.accounts ?? []).map((a) => {
                            const pid = a.platform_id ?? a.platform?.id ?? 0;
                            const fmtFollowers = (n: number) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);
                            return (
                                <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '9px 14px' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                            {PLATFORM_ICON_MAP[pid]}
                                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>{PLATFORM_NAME[pid] ?? a.platform?.name ?? '—'}</span>
                                        </span>
                                    </td>
                                    <td style={{ padding: '9px 14px', maxWidth: 260 }}>
                                        <div>
                                        {a.key ? (
                                            <Typography.Link href={a.key} target="_blank" style={{ fontSize: 13 }}>
                                                {a.title || a.platform_account_id || a.key}
                                            </Typography.Link>
                                        ) : (
                                            <span>{a.title || a.platform_account_id || '—'}</span>
                                        )}
                                        {a.bio && (
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>{a.bio}</div>
                                        )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, fontSize: 14, borderLeft: '1px solid #f0f0f0' }}>
                                        {a.following != null ? fmtFollowers(a.following) : <span style={{ color: '#bbb', fontWeight: 400 }}>—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 16, border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                    {[
                        { label: 'Creator Name', value: creator?.creator },
                        { label: 'Main Platform', value: ({ 1: 'YouTube', 2: 'Instagram', 3: 'TikTok', 4: 'Unknown' } as Record<number, string>)[creator?.main_platform_id ?? 4] },
                        {
                            label: 'Contact Email',
                            value: (creator?.contacts ?? [])
                                .map((c) => c.primary_email?.email_address)
                                .filter((e): e is string => !!e)
                                .join(', ') || undefined,
                        },
                        {
                            label: 'Phone',
                            value: (creator?.contacts ?? [])
                                .map((c) => c.primary_phone_number?.number)
                                .filter((p): p is string => !!p)
                                .join(', ') || undefined,
                        },
                    ].map(({ label, value }, i) => (
                        <div key={label} style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            borderRight: i % 2 === 0 ? '1px solid #f0f0f0' : 'none',
                            background: '#fff',
                        }}>
                            <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                            <div style={{ fontWeight: 500, fontSize: 14 }}>{value || '—'}</div>
                        </div>
                    ))}
                </div>

                {/* Comments */}
                {creator?.reason && (
                    <div style={{ padding: '12px 16px', marginBottom: 16, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fff' }}>
                        <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Comments</div>
                        <div style={{ fontSize: 14, color: '#333', whiteSpace: 'pre-wrap' }}>{creator.reason}</div>
                    </div>
                )}

                {/* Tags table */}
                {transformedTagOptions.some(cat => cat.children.some(c => c.checked)) && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                                <th style={{ padding: '7px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', width: '35%' }}>Category</th>
                                <th style={{ padding: '7px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '1px solid #f0f0f0' }}>Values</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transformedTagOptions
                                .filter(cat => cat.children.some(c => c.checked))
                                .map((cat, i, arr) => (
                                    <tr key={cat.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none', background: '#fff' }}>
                                        <td style={{ padding: '9px 14px', fontWeight: 500, fontSize: 13, color: '#333' }}>{cat.label}</td>
                                        <td style={{ padding: '9px 14px', borderLeft: '1px solid #f0f0f0' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {cat.children.filter(c => c.checked).map(c => {
                                                    const palette = c.color ? generate(c.color) : [];
                                                    return <Tag key={c.value} color={c.color} style={{ margin: 0, color: getPaletteColor(c.color, palette) }}>{c.label}</Tag>;
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                )}

                {/* Linked Connections */}
                {(creator?.contacts ?? []).length > 0 && (
                    <div style={S_CARD}>
                        <div style={S_TITLE}>Linked Connections</div>
                        <ContactLinkField editable={false} initialContacts={creator?.contacts ?? null} />
                    </div>
                )}

                {/* Pricings */}
                <div style={S_CARD}>
                    <div style={S_TITLE}>Pricings</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                        {([
                            { label: 'YT Video',      value: creator?.pricing?.youtube_video,        accent: YT_COLOR },
                            { label: 'TT Video',      value: creator?.pricing?.tiktok_video,         accent: TT_COLOR },
                            { label: 'IG Reel',       value: creator?.pricing?.instagram_reel,       accent: IG_COLOR },
                            { label: 'IG Post',       value: creator?.pricing?.instagram_post,       accent: IG_COLOR },
                            { label: 'IG Story',      value: creator?.pricing?.instagram_story,      accent: IG_COLOR },
                            { label: 'Exclusivity',   value: creator?.pricing?.exclusivity,          accent: '#8c8c8c' },
                            { label: 'Paid Ads',      value: creator?.pricing?.paid_ads_sponsorship, accent: '#8c8c8c' },
                            { label: 'Collaboration', value: creator?.pricing?.collaboration,        accent: '#8c8c8c' },
                        ] as { label: string; value: number | undefined; accent: string }[]).map(({ label, value, accent }) => (
                            <div key={label} style={{
                                border: '1px solid #f0f0f0',
                                borderTop: `3px solid ${accent}`,
                                borderRadius: 8,
                                padding: '10px 12px',
                                background: '#fff',
                            }}>
                                <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                                <div style={{ fontWeight: 700, fontSize: 18, color: '#222' }}>
                                    {value ? `€${value.toLocaleString()}` : <span style={{ color: '#ccc', fontWeight: 400 }}>—</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>{/* end info tab */}
        </>
    );
};

export default CreatorDetailContent;
